import React, { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import FeaturesView from "./components/FeaturesView";
import NotFoundPage from "./components/NotFoundPage";
import FlowsView from "./components/flows/FlowsView";
import ActionsView from "./components/actions/ActionsView";
import BreadcrumbsComponent from "./components/sharedComponents/BreadcrumbsComponent";
import Login from "./components/login";
import URLRedirection from "./components/ai-agents/URLRedirection";
import AgentChatWidget from "./components/ai-agents/AgentChatWidget";
import JobrockPublic from "./components/JobrockPublic";
import { getSession } from "./service/ApiService";

const ADMIN_PATHS = ["/", "/featuresView", "/flowsView", "/actionsView", "/login"];

const AppRoutes = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // The public candidate widget shares this router, so only pay for a session lookup
  // on the admin screens.
  const isAdminPath = ADMIN_PATHS.includes(location.pathname);

  useEffect(() => {
    if (!isAdminPath) {
      return;
    }

    // The session cookie is HttpOnly, so the server is the only thing that can answer
    // this. Treat the result as presentation only — the BFF enforces the session on
    // every request regardless of what this component decides to render.
    const checkAuthentication = async () => {
      try {
        const session = await getSession();
        setIsAuthenticated(Boolean(session?.authenticated));
      } catch {
        setIsAuthenticated(false);
      }
    };

    checkAuthentication();
  }, [navigate, isAdminPath]);

  
  const shouldShowBreadcrumbs = useMemo(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const isAgentChatWidgetRoute = pathSegments.length >= 2;
    return isAuthenticated && !isAgentChatWidgetRoute;
  }, [location.pathname, isAuthenticated]);

  // Undefined until the session lookup resolves; rendering a redirect before then
  // bounces the user to /login and straight back again on every page load.
  const guard = (element: React.ReactElement) => {
    if (isAuthenticated === null) {
      return null;
    }
    return isAuthenticated ? element : <Navigate to="/login" replace />;
  };

  return (
    <>
      {shouldShowBreadcrumbs && <BreadcrumbsComponent />}
      <Routes>
        {/* Public Routes */}
        <Route path=":id" element={<URLRedirection />} />
        <Route path=":source/:uniqueId" element={<AgentChatWidget />} />
        <Route path=":source/:uniqueId/:version" element={<AgentChatWidget />} />
        <Route path="/jrpublic" element={<JobrockPublic />} />

        {/* Protected Routes */}
        <Route path="/" element={<Navigate to="/featuresView" />} />
        <Route path="/featuresView" element={guard(<FeaturesView />)} />
        <Route path="/flowsView" element={guard(<FlowsView />)} />
        <Route path="/actionsView" element={guard(<ActionsView />)} />

        {/* Login Route (should redirect to dashboard if already authenticated) */}
        <Route path="/login" element={isAuthenticated ? <Navigate to="/featuresView" replace /> : <Login />} />

        {/* Catch-all Route for NotFoundPage */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
};

export default AppRoutes;
